/**
 * RoleStatusTab.jsx — RC 1.62 Phase E.
 *
 * Operational governance/status surface for a single employer role.
 * Replaces the Phase-A placeholder shell with a calm institutional
 * read-only view composed from existing, audited data sources only.
 *
 * STRICT DOCTRINE:
 *   • READ-ONLY. No mutating affordance, no Reopen/Close/Archive
 *     button, no governance-pack actions.
 *   • NO HARDCODED NUMBERS. Every count is derived from a live
 *     endpoint or the loaded role object. When data is unavailable,
 *     the card renders an honest "Unavailable" or "Not yet active"
 *     line — never a placeholder zero.
 *   • NO Signals / matching coupling. Phase D remains
 *     dormant.
 *   • Composition only: reuses ReviewerCoveragePanel and
 *     LifecycleTimelinePanel verbatim (already audited governance
 *     surfaces).
 *
 * Data sources (all pre-existing, no new endpoints introduced):
 *   • role  prop                                              — lifecycle, timestamps
 *   • GET /api/employer/v1/roles/{id}/candidates              — intake + evaluation aggregation
 *   • GET /api/employer/v1/roles/{id}/governance-packs        — pack count
 *   • GET /api/employer/v1/audit?role_id=…  (inside Timeline) — events
 *   • GET /api/employer/v1/roles/{id}/reviewer-coverage       — already inside ReviewerCoveragePanel
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Briefcase, ClipboardCheck, Inbox, Loader2,
  RefreshCw, ShieldCheck,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { isActiveRoleStatus } from '@/lib/roleLifecycle';
import { useChannel2 } from '@/lib/useChannel2';
import RoleStatusBadge from './RoleStatusBadge';
import ReviewerCoveragePanel from './ReviewerCoveragePanel';
import LifecycleTimelinePanel from './LifecycleTimelinePanel';

function fmtTs(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year:   'numeric',
    month:  'short',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

/** Honest placeholder for a metric whose source has not yet emitted. */
function StatLine({ label, value, fallback = 'Unavailable', testId }) {
  const display = (value === null || value === undefined) ? fallback : value;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-secondary">
        {label}
      </span>
      <span
        className="text-sm font-semibold text-ndor-ink tabular-nums text-right"
        data-testid={testId}
      >
        {display}
      </span>
    </div>
  );
}

export default function RoleStatusTab({ role }) {
  const roleId = role?.id;
  // RC 1.62 Phase D — read the live Channel-2 enablement projection
  // off /me so the rollout pill flips dynamically. Pure read.
  const channel2 = useChannel2();
  const [candidates, setCandidates]    = useState(null);  // null = loading, [] = loaded empty
  const [packs, setPacks]              = useState(null);
  const [error, setError]              = useState(null);
  const [refreshTok, setRefreshTok]    = useState(0);

  const load = useCallback(async () => {
    if (!roleId) return;
    setError(null);
    try {
      const [candData, packsData] = await Promise.all([
        apiFetch(`/api/employer/v1/roles/${roleId}/candidates`).catch(() => null),
        apiFetch(`/api/employer/v1/roles/${roleId}/governance-packs`).catch(() => null),
      ]);
      setCandidates(Array.isArray(candData?.items) ? candData.items : (candData ? [] : null));
      setPacks(Array.isArray(packsData?.items) ? packsData.items : (packsData ? [] : null));
    } catch (err) {
      setError(err?.message || 'Unable to load role status.');
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load, refreshTok]);

  // ── Derived counters (honest — null when source unavailable) ──
  const intake = useMemo(() => {
    if (!Array.isArray(candidates)) return null;
    let parsed = 0, failed = 0, latest = null;
    for (const c of candidates) {
      if (c.parse_status === 'parsed') parsed += 1;
      else if (c.parse_status && c.parse_status !== 'parsed') failed += 1;
      const ts = c.created_at || c.uploaded_at || null;
      if (ts && (!latest || ts > latest)) latest = ts;
    }
    return {
      total:  candidates.length,
      parsed,
      failed,
      latest,
    };
  }, [candidates]);

  const evals = useMemo(() => {
    if (!Array.isArray(candidates)) return null;
    let complete = 0;
    for (const c of candidates) {
      if (c.evaluation_status === 'complete') complete += 1;
    }
    return { complete };
  }, [candidates]);

  const packsCount = Array.isArray(packs) ? packs.length : null;
  const packsFinalised = Array.isArray(packs)
    ? packs.filter((p) => p.status === 'finalised' || p.status === 'exported').length
    : null;

  if (!role) {
    return (
      <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-status-tab-loading">
        <CardContent className="px-6 py-6">
          <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
        </CardContent>
      </Card>
    );
  }

  const active     = isActiveRoleStatus(role.status);
  const published  = Boolean(role.published_at);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8" data-testid="role-status-tab">
      {/* ── Left column: 3 stacked operational cards ── */}
      <div className="space-y-6 lg:col-span-2 lg:space-y-6">

        {/* A — Role Status ───────────────────────────────────── */}
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-status-card-overview">
          <CardHeader className="pb-3 flex flex-row items-center justify-between px-6 pt-6">
            <CardTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-slate-300" />
              Role Status
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRefreshTok((t) => t + 1)}
              aria-label="Refresh status"
              data-testid="role-status-refresh"
              className="h-8 w-8 p-0"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-1">
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-secondary">
                Lifecycle
              </span>
              <div data-testid="role-status-lifecycle">
                <RoleStatusBadge status={role.status} />
              </div>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-secondary">
                Active for matching
              </span>
              <Badge
                variant="outline"
                className={
                  active
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                    : 'border-ndor-edge bg-ndor-surface-alt/60 text-ndor-ink-secondary'
                }
                data-testid="role-status-active"
              >
                {active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-secondary">
                Publish state
              </span>
              <Badge
                variant="outline"
                className={
                  published
                    ? 'border-blue-500/40 bg-blue-500/10 text-blue-200'
                    : 'border-ndor-edge bg-ndor-surface-alt/60 text-ndor-ink-secondary'
                }
                data-testid="role-status-publish"
              >
                {published ? 'Published' : 'Unpublished'}
              </Badge>
            </div>
            <StatLine
              label="Published at"
              value={fmtTs(role.published_at)}
              fallback="Not published"
              testId="role-status-published-at"
            />
            <StatLine
              label="Created"
              value={fmtTs(role.created_at)}
              testId="role-status-created-at"
            />
            <StatLine
              label="Last updated"
              value={fmtTs(role.updated_at)}
              testId="role-status-updated-at"
            />
          </CardContent>
        </Card>

        {/* B — Intake Status ─────────────────────────────────── */}
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-status-card-intake">
          <CardHeader className="pb-3 px-6 pt-6">
            <CardTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
              <Inbox className="h-4 w-4 text-slate-300" />
              Intake Status
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-1">
            {error && (
              <div className="text-xs text-rose-400 flex items-start gap-2 mb-3" data-testid="role-status-error">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <StatLine
              label="Dossiers uploaded"
              value={intake ? intake.total : null}
              testId="role-status-dossiers-total"
            />
            <StatLine
              label="Parsed"
              value={intake ? intake.parsed : null}
              testId="role-status-dossiers-parsed"
            />
            <StatLine
              label="Parse failed"
              value={intake ? intake.failed : null}
              testId="role-status-dossiers-failed"
            />
            <StatLine
              label="Latest upload"
              value={intake ? fmtTs(intake.latest) : null}
              fallback={intake && intake.total === 0 ? 'No uploads yet' : 'Unavailable'}
              testId="role-status-latest-upload"
            />
          </CardContent>
        </Card>

        {/* C — Evaluation Status ─────────────────────────────── */}
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-status-card-evaluation">
          <CardHeader className="pb-3 px-6 pt-6">
            <CardTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-slate-300" />
              Evaluation Status
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-1">
            <StatLine
              label="Evaluations completed"
              value={evals ? evals.complete : null}
              testId="role-status-evals-complete"
            />
            <StatLine
              label="Governance packs"
              value={packsCount}
              testId="role-status-packs-total"
            />
            <StatLine
              label="Finalised / exported"
              value={packsFinalised}
              testId="role-status-packs-finalised"
            />
            <div className="pt-2 mt-1 border-t border-ndor-edge/50">
              <p className="text-xs text-ndor-ink-muted leading-relaxed">
                Reviewer coverage detail appears in the sidebar — assigned,
                pending and in-review reviewers for this role.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Right column: reviewer coverage + audit timeline ── */}
      <aside className="space-y-6">
        <div data-testid="role-status-reviewer-coverage-mount">
          <ReviewerCoveragePanel roleId={roleId} />
        </div>
        <div data-testid="role-status-timeline-mount">
          <LifecycleTimelinePanel roleId={roleId} />
        </div>
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-status-card-channel-2">
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold text-ndor-ink flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-300" />
              Surfaced Candidates
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-2">
            <Badge
              variant="outline"
              className={channel2.enabled
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-[0.14em]'
                : 'border-ndor-edge bg-ndor-surface-alt/60 text-[10px] uppercase tracking-[0.14em] text-ndor-ink-secondary'}
              data-testid="role-status-channel-2-pill"
            >
              {channel2.enabled ? 'Active · Channel-2' : 'Not yet active'}
            </Badge>
            {channel2.enabled ? (
              <>
                <p className="text-xs text-ndor-ink leading-relaxed">
                  Anonymous role-scoped matching is active on this workspace.
                  Open the Candidates tab → Surfaced Candidates sub-tab to
                  review matches and send governed signals.
                </p>
                {channel2.signals_remaining !== null && (
                  <p className="text-[11px] text-ndor-ink-secondary tabular-nums" data-testid="role-status-channel-2-quota">
                    Signals remaining: {channel2.signals_remaining}
                    {channel2.signals_allowance !== null ? ` / ${channel2.signals_allowance}` : ''}
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-ndor-ink-muted leading-relaxed">
                Anonymous role-scoped matching is preview-only on this
                workspace. When it rolls out broadly, candidate-side
                acceptance flows will be auditable from the Lifecycle
                Timeline.
              </p>
            )}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

/* eslint-disable react/prop-types */
RoleStatusTab.displayName = 'RoleStatusTab';
